"use client"

import { http } from "@/shared/lib/api/http"
import {
  WorkQueueEntityType,
  WorkQueueItemType,
  WorkQueuePriority,
  WorkQueueScopeKind,
  type WorkQueueItem,
  type WorkQueueItemsPage,
  type WorkQueueSummary,
} from "@/features/work-queue/model/work-queue.types"

type RawWorkQueueItem = Partial<Record<keyof WorkQueueItem, unknown>> & { metadata?: unknown }
type RawSummary = {
  totalCount?: unknown
  groups?: Array<{ type?: unknown; count?: unknown; oldestSubmittedAt?: unknown }>
}
type RawItemsResponse = {
  items?: RawWorkQueueItem[]
  total?: unknown
  page?: unknown
  limit?: unknown
  totalPages?: unknown
}

function positiveInt(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 1 ? Math.floor(value) : fallback
}

function toEnum<T extends Record<string, string>>(enumObj: T, value: unknown, fallback: T[keyof T]): T[keyof T] {
  return typeof value === "string" && (Object.values(enumObj) as string[]).includes(value)
    ? (value as T[keyof T])
    : fallback
}

function nullableString(value: unknown): string | null {
  return value == null ? null : String(value)
}

function normalizeItem(raw: RawWorkQueueItem): WorkQueueItem {
  const type = toEnum(WorkQueueItemType, raw.type, WorkQueueItemType.BudgetReview)
  return {
    id: String(raw.id ?? ""),
    type,
    categoryLabelKey: typeof raw.categoryLabelKey === "string" ? raw.categoryLabelKey : `workQueue.types.${type}`,
    entityType: toEnum(WorkQueueEntityType, raw.entityType, WorkQueueEntityType.Budget),
    entityId: String(raw.entityId ?? ""),
    title: String(raw.title ?? ""),
    description: nullableString(raw.description),
    status: String(raw.status ?? ""),
    priority: toEnum(WorkQueuePriority, raw.priority, WorkQueuePriority.Normal),
    scopeKind: toEnum(WorkQueueScopeKind, raw.scopeKind, WorkQueueScopeKind.Branch),
    workspaceId: String(raw.workspaceId ?? ""),
    branchId: nullableString(raw.branchId),
    assignedUserId: nullableString(raw.assignedUserId),
    submittedByUserId: nullableString(raw.submittedByUserId),
    submittedByName: nullableString(raw.submittedByName),
    submittedByAvatarUrl: nullableString(raw.submittedByAvatarUrl),
    submittedAt: nullableString(raw.submittedAt),
    actionUrl: String(raw.actionUrl ?? ""),
    metadata: raw.metadata && typeof raw.metadata === "object" ? (raw.metadata as Record<string, unknown>) : {},
  }
}

function normalizeSummary(raw: RawSummary | undefined): WorkQueueSummary {
  return {
    totalCount: typeof raw?.totalCount === "number" ? raw.totalCount : 0,
    groups: (raw?.groups ?? []).map((group) => ({
      type: toEnum(WorkQueueItemType, group.type, WorkQueueItemType.BudgetReview),
      count: typeof group.count === "number" ? group.count : 0,
      oldestSubmittedAt: typeof group.oldestSubmittedAt === "string" ? group.oldestSubmittedAt : null,
    })),
  }
}

/** `branchId` narrows every group to that branch (the branch home dashboard); omit it for the whole queue. */
export async function getWorkQueueSummary(workspaceId: string, branchId?: string): Promise<WorkQueueSummary> {
  const response = await http.get<RawSummary>("/work-queue/summary", {
    params: { workspaceId, ...(branchId ? { branchId } : {}) },
  })
  return normalizeSummary(response.data)
}

export type ListWorkQueueItemsParams = {
  type?: WorkQueueItemType
  page?: number
  limit?: number
}

/** One page of the queue, optionally narrowed to one item type. The server pages after it authorizes. */
export async function listWorkQueueItems(
  workspaceId: string,
  params: ListWorkQueueItemsParams = {},
): Promise<WorkQueueItemsPage> {
  const response = await http.get<RawItemsResponse>("/work-queue/items", {
    params: {
      workspaceId,
      ...(params.type ? { type: params.type } : {}),
      ...(params.page ? { page: params.page } : {}),
      ...(params.limit ? { limit: params.limit } : {}),
    },
  })
  const data = response.data
  const items = (data?.items ?? []).map(normalizeItem)
  const total = typeof data?.total === "number" ? data.total : items.length
  const limit = positiveInt(data?.limit, params.limit ?? Math.max(items.length, 1))
  return {
    items,
    total,
    page: positiveInt(data?.page, params.page ?? 1),
    limit,
    totalPages: positiveInt(data?.totalPages, Math.max(1, Math.ceil(total / limit))),
  }
}
